import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Support from "../components/Support";
import { SITE } from "../data/siteData";

export default function AuthPage() {
  const { login, register, currentUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(
    params.get("mode") === "register" ? "register" : "login",
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser) navigate("/course");
  }, [currentUser, navigate]);

  const reset = () => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setShowPassword(false);
    setShowConfirm(false);
  };

  const validate = () => {
    if (!email.trim()) return "Please enter your email address.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return "Please enter a valid email address.";
    if (mode === "register" && !name.trim())
      return "Please enter your full name.";
    if (!password) return "Please enter a password.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    if (mode === "register" && password !== confirmPassword)
      return "Passwords do not match.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setLoading(true);
    const result =
      mode === "login"
        ? await login(email.trim(), password)
        : await register(email.trim(), password, name.trim());
    setLoading(false);
    if (result.ok) navigate("/course");
    else setError(result.error);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f5f5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 16px",
        fontFamily: "Segoe UI, Arial, sans-serif",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <span
            onClick={() => navigate("/")}
            style={{
              fontWeight: 900,
              fontSize: 24,
              color: "#009dc4",
              letterSpacing: -0.5,
              cursor: "pointer",
            }}
          >
            {SITE.name}
          </span>
          <p style={{ color: "#666", fontSize: 13, marginTop: 5 }}>
            {mode === "login"
              ? "Sign in to continue your course"
              : "Create your free account to get started"}
          </p>
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderTop: "3px solid #009dc4",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", borderBottom: "1px solid #e0e0e0" }}>
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => {
                  reset();
                  setMode(m);
                }}
                style={{
                  flex: 1,
                  padding: "12px 0",
                  background: mode === m ? "#fff" : "#fafafa",
                  border: "none",
                  borderBottom:
                    mode === m ? "2px solid #009dc4" : "2px solid transparent",
                  color: mode === m ? "#009dc4" : "#888",
                  fontWeight: mode === m ? 700 : 400,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <div style={{ padding: "22px 22px" }}>
            {mode === "register" && (
              <Field
                label="Full name"
                value={name}
                onChange={setName}
                placeholder="e.g. Jane Mwangi"
                autoComplete="name"
              />
            )}
            <Field
              label="Email address"
              value={email}
              onChange={setEmail}
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
            />
            <PwField
              label="Password"
              value={password}
              onChange={setPassword}
              show={showPassword}
              onToggle={() => setShowPassword((v) => !v)}
              placeholder={
                mode === "register" ? "At least 6 characters" : "Your password"
              }
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              onKeyDown={
                mode === "login"
                  ? (e) => e.key === "Enter" && submit()
                  : undefined
              }
            />
            {mode === "register" && (
              <PwField
                label="Confirm password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                show={showConfirm}
                onToggle={() => setShowConfirm((v) => !v)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            )}
            {error && (
              <div
                style={{
                  background: "#fff5f5",
                  border: "1px solid #fca5a5",
                  borderLeft: "3px solid #dc2626",
                  borderRadius: 3,
                  padding: "9px 12px",
                  marginBottom: 14,
                }}
              >
                <p style={{ color: "#dc2626", fontSize: 13, margin: 0 }}>
                  {error}
                </p>
              </div>
            )}
            <button
              onClick={submit}
              disabled={loading}
              style={{
                width: "100%",
                padding: "11px 0",
                background: loading ? "#aaa" : "#009dc4",
                border: "none",
                borderRadius: 4,
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {loading
                ? "Please wait..."
                : mode === "login"
                  ? "Sign in \u2192"
                  : "Create account \u2192"}
            </button>
            {mode === "login" && (
              <p
                style={{
                  textAlign: "center",
                  fontSize: 12,
                  color: "#999",
                  marginTop: 14,
                  marginBottom: 0,
                }}
              >
                Forgot password? Email{" "}
                <a
                  href={"mailto:" + SITE.supportEmail}
                  style={{ color: "#009dc4" }}
                >
                  {SITE.supportEmail}
                </a>
              </p>
            )}
          </div>
        </div>
        <Support style={{ marginTop: 16 }} />
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  onKeyDown,
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        style={inStyle}
      />
    </div>
  );
}

function PwField({
  label,
  value,
  onChange,
  show,
  onToggle,
  placeholder,
  autoComplete,
  onKeyDown,
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lStyle}>{label}</label>
      <div style={{ position: "relative" }}>
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onKeyDown={onKeyDown}
          style={{ ...inStyle, paddingRight: 44 }}
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 40,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#888",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );
}

const lStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#282a35",
  marginBottom: 5,
};
const inStyle = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: 14,
  color: "#282a35",
  background: "#fff",
  boxSizing: "border-box",
  fontFamily: "inherit",
  outline: "none",
};
